import { useState, useEffect } from "react";
import { NavLink, Routes, Route, useLocation } from "react-router-dom";
import { Activity, FileBarChart, CalendarRange, Plane, ListChecks, Users, Settings as Cog, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import Requests from "./Requests";
import { Header } from "@/components/kv";
import { useT } from "@/lib/i18n";
import Dashboard from "./Dashboard";
import Report from "./Report";
import Planner from "./Planner";
import Leaves from "./Leaves";
import TasksAdmin from "./TasksAdmin";
import StaffAdmin from "./StaffAdmin";
import Settings from "./Settings";

const NAV = [
  { to: "/admin", key: "dashboard", icon: Activity, end: true },
  { to: "/admin/requests", key: "requests", icon: Inbox },
  { to: "/admin/report", key: "report", icon: FileBarChart },
  { to: "/admin/planner", key: "planner", icon: CalendarRange },
  { to: "/admin/leaves", key: "leaves", icon: Plane },
  { to: "/admin/tasks", key: "tasks", icon: ListChecks },
  { to: "/admin/staff", key: "staff", icon: Users },
  { to: "/admin/settings", key: "settings", icon: Cog },
];

export default function AdminApp() {
  const { t } = useT();
  const loc = useLocation();
  const [pending, setPending] = useState(0);
  useEffect(() => {
    const get = () => api.get("/admin/requests/count").then((r) => setPending(r.data.pending)).catch(() => {});
    get();
    const id = setInterval(get, 30000);
    window.addEventListener("kvp-requests-changed", get);
    return () => {
      clearInterval(id);
      window.removeEventListener("kvp-requests-changed", get);
    };
  }, [loc.pathname]);
  const link = ({ isActive }) =>
    `flex items-center gap-2.5 whitespace-nowrap rounded-full lg:rounded-xl px-4 py-2 text-sm font-medium transition-colors ${isActive ? "bg-[#2c1810] text-amber-50" : "text-stone-600 hover:bg-[#F3EDE2]"}`;
  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:flex lg:gap-8">
        <nav className="lg:w-56 shrink-0 lg:pt-8">
          <div className="flex lg:flex-col gap-1 overflow-x-auto py-3 lg:py-0 lg:sticky lg:top-24 -mx-4 px-4 lg:mx-0 lg:px-0" data-testid="admin-nav">
            {NAV.map(({ to, key, icon: Icon, end }) => (
              <NavLink key={key} to={to} end={end} className={link} data-testid={`admin-nav-${key}`}>
                <Icon className="h-4 w-4" />
                {t(`adminnav.${key}`)}
                {key === "requests" && pending > 0 && (
                  <span className="ml-auto rounded-full bg-[#b8563a] text-white text-[11px] font-bold px-2 py-0.5" data-testid="requests-badge">{pending}</span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
        <main className="flex-1 min-w-0 pt-4 lg:pt-8 pb-16">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="requests" element={<Requests />} />
            <Route path="report" element={<Report />} />
            <Route path="planner" element={<Planner />} />
            <Route path="leaves" element={<Leaves />} />
            <Route path="tasks" element={<TasksAdmin />} />
            <Route path="staff" element={<StaffAdmin />} />
            <Route path="settings" element={<Settings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
